import { useCallback, useEffect, useState } from "react";
import { demoApi, problemMessage } from "@/api/client";
import type { CreateShiftRequest, Guid, Shift, UpdateShiftRequest } from "@/api/types";

export type ScheduleStatus = "loading" | "ready" | "error";

/**
 * How far ahead the board looks, in days. Bounded well under `ScheduleEndpoints.
 * MaxWindowDays` (366) — a farmer plans a season, not a decade, and a wide-but-bounded
 * window (rather than one clamped to a literal 7-day "this week") is what lets a shift
 * created for next month show up on this same screen the moment it is saved, with no
 * reload and no "go find the right week" step (schedule.spec.ts's own AC2).
 */
const SCHEDULE_WINDOW_DAYS = 180;

/** Small trailing buffer so a shift that started earlier today (or a few hours ago, in
 *  a timezone this browser reads differently from the server) is not silently dropped
 *  off the front of the window. */
const WINDOW_LOOKBACK_DAYS = 1;

export interface UseScheduleResult {
  /** `null` until resolved and on failure. Never re-sorted here — Schedule.tsx groups
   *  and orders this itself, once, per render. */
  shifts: Shift[] | null;
  status: ScheduleStatus;
  error: string | null;
  reload: () => Promise<void>;
  /** Throws on failure — Schedule.tsx catches this itself to read the server's own
   *  field errors off it (`problemFieldErrors`), which this hook has no opinion about. */
  create: (body: CreateShiftRequest) => Promise<Shift>;
  update: (id: Guid, body: UpdateShiftRequest) => Promise<Shift>;
  remove: (id: Guid) => Promise<void>;
}

/**
 * ef-040-3 — the owner's schedule board: everyone's shifts across a bounded rolling
 * window, GET /api/demo/schedule/shifts.
 *
 * Owner-only server-side (403 for an employee login). This hook does not check that and
 * must not: a client-side role check is a rendering decision, and treating it as the
 * control is how the real one stops being maintained — same reasoning `usePendingTime`
 * and `usePlaces` give. The Schedule screen this feeds is already behind the owner shell.
 *
 * Re-reads the WHOLE window after every write rather than patching the array in place,
 * same convention `usePlaces` uses and for the same reason: the list here is bounded and
 * a farm's own board, not a growing transactional table, so a full re-read can never
 * drift from what the server actually holds.
 */
export function useSchedule(): UseScheduleResult {
  const [shifts, setShifts] = useState<Shift[] | null>(null);
  const [status, setStatus] = useState<ScheduleStatus>("loading");
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (signal?: AbortSignal): Promise<void> => {
    try {
      const from = new Date();
      from.setDate(from.getDate() - WINDOW_LOOKBACK_DAYS);
      const to = new Date();
      to.setDate(to.getDate() + SCHEDULE_WINDOW_DAYS);
      const resolved = await demoApi.getShifts(
        { from: from.toISOString(), to: to.toISOString() },
        undefined,
        signal,
      );
      if (signal?.aborted) return;
      setShifts(resolved);
      setStatus("ready");
      setError(null);
    } catch (err) {
      if (signal?.aborted || (err instanceof DOMException && err.name === "AbortError")) return;
      setShifts(null);
      setStatus("error");
      setError(problemMessage(err, "Could not load the schedule."));
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const reload = useCallback(() => load(), [load]);

  const create = useCallback(
    async (body: CreateShiftRequest): Promise<Shift> => {
      const shift = await demoApi.createShift(body);
      await load();
      return shift;
    },
    [load],
  );

  const update = useCallback(
    async (id: Guid, body: UpdateShiftRequest): Promise<Shift> => {
      const shift = await demoApi.updateShift(id, body);
      await load();
      return shift;
    },
    [load],
  );

  const remove = useCallback(
    async (id: Guid): Promise<void> => {
      await demoApi.deleteShift(id);
      await load();
    },
    [load],
  );

  return { shifts, status, error, reload, create, update, remove };
}
